import { Modal } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useState } from "react";

import type { SiteEntity } from "src/app/features/Site/types/SiteEntity";
import { useUpdateTableDataMutation } from "../../../../../components";
import { PrimaryButton, SecondaryButton } from "../../../../../components/Buttons/components/ActionButton";
import { RenderImportResult } from "../../../../../components/FeatureButtons/csvModalButtonUtils";
import type { ButtonProps, ImportResult } from "../../../../../components/FeatureButtons/types";
import { SelectDynamic } from "../../../../../components/Form/formComponents";
import { dataGroupSelectorConfig } from "../../../../../components/SearchForm/FormSearchUtils";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const MoveSiteModal: FC<ButtonProps<SiteEntity>> = ({ isDisabled, items }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [destinationDataGroupId, setDestinationDataGroupId] = useState<string | null>();
    const [uploading, setUploading] = useState(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [showAllErrors, setShowAllErrors] = useState(false);

    const currentDataGroup = getAssignedDataGroup();
    const moveUrl = `${ResourceName.DATA_GROUPS}/${currentDataGroup.code}/${ResourceName.SITES}/${FORM_ACTION.MOVE}`;

    const isDisabledFinal = isDisabled || items?.length === 0;

    const [moveDataRequest] = useUpdateTableDataMutation();

    const showModal = useCallback(() => setIsModalVisible(true), []);
    const onCancel = useCallback(() => setIsModalVisible(false), []);

    const onOk = useCallback(async () => {
        setUploading(true);

        try {
            const body = {
                orgUnitIds: items?.map((item) => item.siteId).join(","),
                dataGroupId: Number(destinationDataGroupId),
            };

            moveDataRequest({
                url: moveUrl,
                method: "POST",
                body,
            })
                .unwrap()
                // @ts-ignore
                .then((result) => setImportResult(result))
                .catch((error) => {
                    setImportResult({
                        importErrors: [error?.data?.message || "moveData.modal.importError"],
                    });
                })
                .finally(() => setUploading(false));
        } catch (error) {
            setImportResult({
                importErrors: [typeof error === "string" ? error : "moveData.modal.importError"],
            });
            setUploading(false);
        }
    }, [destinationDataGroupId, items, moveDataRequest, moveUrl]);

    const onSelectedDataGroupId = useCallback(
        (dataGroup: string) => {
            if (dataGroup) {
                setDestinationDataGroupId(dataGroup);
            }
        },
        [setDestinationDataGroupId],
    );

    return (
        <>
            <SecondaryButton onClick={showModal} disabled={isDisabledFinal}>
                {i18n.t("button.move")}
            </SecondaryButton>

            <Modal
                open={isModalVisible}
                title={i18n.t("moveData.modal.title")}
                onOk={onOk}
                onCancel={onCancel}
                footer={[
                    <SecondaryButton key="close" onClick={onCancel}>
                        {i18n.t("common.close")}
                    </SecondaryButton>,
                    <PrimaryButton key="import" onClick={onOk} disabled={uploading}>
                        {i18n.t("button.save")}
                    </PrimaryButton>,
                ]}
            >
                <p>{i18n.t("moveData.modal.message")}</p>
                <SelectDynamic
                    name="dataGroup"
                    label={i18n.t("moveData.dataGroup")}
                    onChange={onSelectedDataGroupId}
                    fieldConfig={dataGroupSelectorConfig}
                    value={destinationDataGroupId || undefined}
                    filterOptions={(option) => option.value !== String(currentDataGroup.id)}
                />
                {importResult && (
                    <RenderImportResult importResult={importResult} showAllErrors={showAllErrors} setShowAllErrors={setShowAllErrors} />
                )}
            </Modal>
        </>
    );
};
